import React from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Alert, // ✅ NEW
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { API_URL } from "./config";
import { useNavigate } from "react-router-dom";

// ✅ AY helper: last 5 + current (India: starts in June)
const getCurrentAcademicYearStart = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  return month < 6 ? year - 1 : year;
};
const getAcademicYearStarts = (pastYears = 5) => {
  const currentStart = getCurrentAcademicYearStart();
  const years = [];
  for (let i = pastYears; i >= 0; i--) years.push(currentStart - i);
  return years;
};
const formatAY = (start) => `${start}-${String(start + 1).slice(-2)}`;

// ----------------------
// ✅ Helpers for month drill-down
// ----------------------
const monthKeyFromISO = (isoDate) => (isoDate ? isoDate.slice(0, 7) : ""); // YYYY-MM
const prettyMonth = (yyyyMm) => {
  if (!yyyyMm) return "";
  const [y, m] = yyyyMm.split("-").map(Number);
  const dt = new Date(y, m - 1, 1);
  return dt.toLocaleString("en-IN", { month: "short", year: "numeric" });
};

const groupDailyToMonthlyAvg = (rows, valueKey) => {
  const map = new Map();
  for (const r of rows || []) {
    const mk = monthKeyFromISO(r.d);
    const v = Number(r[valueKey]);
    if (!mk || Number.isNaN(v)) continue;
    const cur = map.get(mk) || { sum: 0, cnt: 0 };
    cur.sum += v;
    cur.cnt += 1;
    map.set(mk, cur);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([m, agg]) => ({
      m,
      label: prettyMonth(m),
      val: agg.cnt ? +(agg.sum / agg.cnt).toFixed(2) : 0,
    }));
};

const groupDailyToMonthlySum = (rows, valueKey) => {
  const map = new Map();
  for (const r of rows || []) {
    const mk = monthKeyFromISO(r.d);
    const v = Number(r[valueKey]);
    if (!mk || Number.isNaN(v)) continue;
    map.set(mk, (map.get(mk) || 0) + v);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([m, total]) => ({
      m,
      label: prettyMonth(m),
      total,
    }));
};

const filterToMonth = (rows, yyyyMm) =>
  (rows || []).filter((r) => monthKeyFromISO(r.d) === yyyyMm);

export default function DeanDashboard() {
  const nav = useNavigate();

  // ✅ Theme constants (same as above pages)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const SURFACE_BORDER = "rgba(25,118,210,0.25)";

  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [data, setData] = React.useState(null);

  // ✅ NEW: latest gist state
  const [loadingGist, setLoadingGist] = React.useState(false);
  const [gistErr, setGistErr] = React.useState("");
  const [latestGist, setLatestGist] = React.useState(null);
  const [latestGistMeta, setLatestGistMeta] = React.useState(null);

  // ✅ Filters
  const academicYears = React.useMemo(() => getAcademicYearStarts(5), []);
  const [ay, setAy] = React.useState(() => getCurrentAcademicYearStart());
  const [term, setTerm] = React.useState("1");
  const [from, setFrom] = React.useState("");
  const [to, setTo] = React.useState("");

  // ✅ Drill-down
  const [attMonth, setAttMonth] = React.useState(null);
  const [feeMonth, setFeeMonth] = React.useState(null);

  // ✅ NEW: load latest gist (recommended dean endpoint)
  const loadLatestGist = React.useCallback(async () => {
    setGistErr("");
    setLoadingGist(true);

    try {
      const token = localStorage.getItem("auth");
      const resp = await axios.get(`${API_URL}/api/dean/latest-feedback-gist`, {
        headers: { Authorization: `Bearer ${token}` },
        // optionally include filters if you want:
        // params: { ay, term, from: from || null, to: to || null }
      });

      // Expected shape:
      // { ok:true, exists:true, meta:{...}, gist:{...} }
      if (resp.data?.exists) {
        const g = resp.data.gist;
        const parsed =
          typeof g === "string"
            ? (() => {
                try { return JSON.parse(g); } catch { return null; }
              })()
            : g;

        setLatestGist(parsed || null);
        setLatestGistMeta(resp.data.meta || null);
      } else {
        setLatestGist(null);
        setLatestGistMeta(null);
      }
    } catch (e) {
      setLatestGist(null);
      setLatestGistMeta(null);
      setGistErr(e?.response?.data?.error || "Failed to load latest AI gist");
    } finally {
      setLoadingGist(false);
    }
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const token = localStorage.getItem("auth");
      const resp = await axios.get(`${API_URL}/api/dean/overview`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { ay, term, from: from || null, to: to || null },
      });
      setData(resp.data);
      setAttMonth(null);
      setFeeMonth(null);

      // ✅ NEW: also refresh the gist whenever dashboard refreshes
      loadLatestGist();
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [ay, term, from, to, loadLatestGist]);

  React.useEffect(() => {
    load();
  }, [load]);

  // ✅ define BEFORE early returns
  const k = data?.kpi || {};
  const attendanceDaily = data?.charts?.attendanceTrend || [];
  const feeDaily = data?.charts?.feeTrend || [];
  const alerts = data?.alerts || {};

  const attendanceMonthly = React.useMemo(
    () => groupDailyToMonthlyAvg(attendanceDaily, "pct"),
    [attendanceDaily]
  );

  const feeMonthly = React.useMemo(
    () => groupDailyToMonthlySum(feeDaily, "collected"),
    [feeDaily]
  );

  const attendanceDailyInMonth = React.useMemo(
    () => (attMonth ? filterToMonth(attendanceDaily, attMonth) : []),
    [attendanceDaily, attMonth]
  );

  const feeDailyInMonth = React.useMemo(
    () => (feeMonth ? filterToMonth(feeDaily, feeMonth) : []),
    [feeDaily, feeMonth]
  );

  const money = (n) => {
    const x = Number(n || 0);
    return x.toLocaleString("en-IN", { maximumFractionDigits: 0 });
  };

  // ✅ styles (same patterns used above)
  const surfacePaperSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${SURFACE_BORDER}`,
  };

  const kpiCardSx = (clickable) => ({
    p: 2,
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${SURFACE_BORDER}`,
    position: "relative",
    cursor: clickable ? "pointer" : "default",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
    "&:hover": clickable
      ? { backgroundColor: "rgba(240,120,40,0.03)" }
      : undefined,
  });

  const headerBar = (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box>
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, lineHeight: 1.2 }}>
          Dean Dashboard
        </Typography>
        <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
          Campus: {data?.CampusCode || "-"} • AY: {formatAY(ay)} • Term: {term}
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} sx={{ color: "white" }} />
          <Typography sx={{ fontSize: "0.85rem", opacity: 0.9 }}>Loading…</Typography>
        </Box>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Paper
        elevation={5}
        sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid rgba(25,118,210,0.15)" }}
      >
        {headerBar}
        <Box sx={{ p: 3, backgroundColor: BG, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (err) {
    return (
      <Paper
        elevation={5}
        sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid rgba(25,118,210,0.15)" }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
            color: "white",
          }}
        >
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 800 }}>
            Dean Dashboard
          </Typography>
          <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
            Unable to load dashboard
          </Typography>
        </Box>

        <Box sx={{ p: 3, backgroundColor: BG }}>
          <Paper variant="outlined" sx={{ ...surfacePaperSx, p: 2 }}>
            <Typography color="error" sx={{ fontWeight: 700 }}>
              {err}
            </Typography>
            <Button
              onClick={load}
              sx={{
                mt: 2,
                borderRadius: 2,
                backgroundColor: THEME_ORANGE,
                "&:hover": { backgroundColor: THEME_ORANGE_2 },
              }}
              variant="contained"
            >
              Retry
            </Button>
          </Paper>
        </Box>
      </Paper>
    );
  }

  const KPICard = ({ title, value, sub, onClick }) => (
    <Paper elevation={0} sx={kpiCardSx(!!onClick)} onClick={onClick}>
      <Typography variant="body2" sx={{ opacity: 0.75, pl: 1 }}>
        {title}
      </Typography>
      <Typography
        variant="h5"
        sx={{ fontWeight: 900, mt: 0.5, pl: 1, color: "rgba(25,118,210,0.95)" }}
      >
        {value ?? "-"}
      </Typography>
      {sub ? (
        <Typography variant="caption" sx={{ opacity: 0.8, pl: 1 }}>
          {sub}
        </Typography>
      ) : null}
    </Paper>
  );

  const ChartPanel = ({ title, subtitle, right, children }) => (
    <Paper variant="outlined" sx={{ ...surfacePaperSx, p: 2, height: 360 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>{title}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            {subtitle}
          </Typography>
        </Box>
        {right}
      </Box>
      <Divider sx={{ my: 1.5, borderColor: "rgba(25,118,210,0.12)" }} />
      {children}
    </Paper>
  );

  // ✅ NEW: Latest AI Gist panel (same theme surface + left orange stripe like KPI)
  const LatestGistPanel = () => (
    <Paper
      variant="outlined"
      sx={{
        ...surfacePaperSx,
        p: 2,
        position: "relative",
        overflow: "hidden",
        "&:before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 6,
          background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
          opacity: 0.95,
        },
      }}
    >
      <Box sx={{ pl: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
            Latest AI Feedback Gist
          </Typography>

        </Box>

        {latestGistMeta?.GeneratedAt ? (
          <Typography variant="caption" color="text.secondary">
            Last generated: {new Date(latestGistMeta.GeneratedAt).toLocaleString()} • {latestGistMeta.GenerationMode}
            {latestGistMeta.FormTitle ? ` • ${latestGistMeta.FormTitle}` : ""}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No gist generated yet.
          </Typography>
        )}

        {gistErr ? (
          <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}>
            {gistErr}
          </Alert>
        ) : null}

        {!latestGist ? (
          <Typography color="text.secondary" sx={{ mt: 1.5 }}>
            No saved AI gist found. Generate one from <b>Feedback Analytics</b>.
          </Typography>
        ) : (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>
              {latestGist.gist || latestGist.summary || "—"}
            </Typography>

            {!!(latestGist.positives || []).length && (
              <>
                <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mt: 1 }}>
                  Positives
                </Typography>
                {(latestGist.positives || []).slice(0, 5).map((x, i) => (
                  <Typography key={i} variant="body2">• {x}</Typography>
                ))}
              </>
            )}

            {!!(latestGist.concerns || []).length && (
              <>
                <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mt: 1 }}>
                  Concerns
                </Typography>
                {(latestGist.concerns || []).slice(0, 5).map((x, i) => (
                  <Typography key={i} variant="body2">• {x}</Typography>
                ))}
              </>
            )}

            {!!(latestGist.actionable_recommendations || []).length && (
              <>
                <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mt: 1 }}>
                  Action Plan
                </Typography>
                {(latestGist.actionable_recommendations || []).slice(0, 5).map((x, i) => (
                  <Typography key={i} variant="body2">{i + 1}. {x}</Typography>
                ))}
              </>
            )}

            <Button
              size="small"
              sx={{ mt: 1.5, textTransform: "none", fontWeight: 900 }}
              onClick={() => nav("/FeedbackFormAnalytics")}
            >
              Open Feedback Analytics →
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );

  return (
    <Paper
      elevation={5}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid rgba(25,118,210,0.15)",
      }}
    >
      {headerBar}

      <Box sx={{ p: 2, backgroundColor: BG }}>
        {/* Filters Row */}
        <Paper variant="outlined" sx={{ ...surfacePaperSx, p: 2, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                Filters
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Change Academic Year / Term and refresh overview
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Academic Year</InputLabel>
                <Select
                  label="Academic Year"
                  value={ay}
                  onChange={(e) => setAy(Number(e.target.value))}
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: SURFACE_BORDER },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(25,118,210,0.45)" },
                  }}
                >
                  {academicYears.map((start) => (
                    <MenuItem key={start} value={start}>
                      {formatAY(start)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Term</InputLabel>
                <Select
                  label="Term"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: SURFACE_BORDER },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(25,118,210,0.45)" },
                  }}
                >
                  <MenuItem value="1">1</MenuItem>
                  <MenuItem value="2">2</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={load}
                sx={{
                  borderRadius: 2,
                  backgroundColor: THEME_ORANGE,
                  fontWeight: 900,
                  textTransform: "none",
                  "&:hover": { backgroundColor: THEME_ORANGE_2 },
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* KPI Tiles */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <KPICard
              title="Total Students"
              value={k.TotalStudents}
              sub="Active students"
              onClick={() => nav("/ReportStudentPersonalInformationUpdate")}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <KPICard title="Total Employees" value={k.TotalEmployees} sub="Faculty + Staff" />
          </Grid>
          <Grid item xs={12} md={3}>
            <KPICard
              title="Applications"
              value={k.ApplicationsReceived}
              sub={`Approved: ${k.ApplicationsApproved ?? 0}`}
              onClick={() => nav("/ApproverStudentApplication")}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <KPICard
              title="Fee Due"
              value={`₹ ${money(k.FeeDue)}`}
              sub={`Collected: ₹ ${money(k.FeePaid)} / Demand: ₹ ${money(k.FeeAmount)}`}
              onClick={() => nav("/StudentFeeReport")}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2, borderColor: "rgba(25,118,210,0.12)" }} />

        {/* Charts */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ChartPanel
              title="Attendance Trend"
              subtitle={
                attMonth
                  ? `Daily attendance % • ${prettyMonth(attMonth)}`
                  : "Monthly average attendance % (click a point/month)"
              }
              right={
                attMonth ? (
                  <Button
                    size="small"
                    onClick={() => setAttMonth(null)}
                    sx={{ textTransform: "none", fontWeight: 800 }}
                  >
                    ← Back to months
                  </Button>
                ) : null
              }
            >
              <Box sx={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={attMonth ? attendanceDailyInMonth : attendanceMonthly}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={attMonth ? "d" : "label"} />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey={attMonth ? "pct" : "val"}
                      dot={!attMonth}
                      strokeWidth={3}
                      stroke="rgba(25,118,210,0.75)"
                      activeDot={{
                        r: 6,
                        onClick: (_e, payload) => {
                          const p = payload?.payload;
                          if (!attMonth && p?.m) setAttMonth(p.m);
                        },
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              <Button
                size="small"
                sx={{ mt: 1, textTransform: "none", fontWeight: 800 }}
                onClick={() => nav("/AnalyticsAttendance")}
              >
                Open Attendance Analytics →
              </Button>
            </ChartPanel>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartPanel
              title="Fee Collection Trend"
              subtitle={
                feeMonth
                  ? `Daily collections • ${prettyMonth(feeMonth)}`
                  : "Monthly total collections (click a point/month)"
              }
              right={
                feeMonth ? (
                  <Button
                    size="small"
                    onClick={() => setFeeMonth(null)}
                    sx={{ textTransform: "none", fontWeight: 800 }}
                  >
                    ← Back to months
                  </Button>
                ) : null
              }
            >
              <Box sx={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={feeMonth ? feeDailyInMonth : feeMonthly}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={feeMonth ? "d" : "label"} />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey={feeMonth ? "collected" : "total"}
                      dot={!feeMonth}
                      strokeWidth={3}
                      stroke="rgba(240,120,40,0.75)"
                      activeDot={{
                        r: 6,
                        onClick: (_e, payload) => {
                          const p = payload?.payload;
                          if (!feeMonth && p?.m) setFeeMonth(p.m);
                        },
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              <Button
                size="small"
                sx={{ mt: 1, textTransform: "none", fontWeight: 800 }}
                onClick={() => nav("/StudentFeeReport")}
              >
                Open Fee Reports →
              </Button>
            </ChartPanel>
          </Grid>
        </Grid>

        {/* ✅ NEW: Latest AI Gist section */}
        <Divider sx={{ my: 2, borderColor: "rgba(25,118,210,0.12)" }} />
        <LatestGistPanel />
        <Divider sx={{ my: 2, borderColor: "rgba(25,118,210,0.12)" }} />

        {/* Alerts */}
        <Paper variant="outlined" sx={{ ...surfacePaperSx, p: 2 }}>
          <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
            Alerts
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            Quick risk indicators and drill-down links
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
            <Chip
              label={`Low Attendance (<75%): ${alerts.LowAttendanceCount ?? 0}`}
              sx={{
                border: `1px solid ${SURFACE_BORDER}`,
                backgroundColor: "rgba(240,120,40,0.10)",
                fontWeight: 800,
              }}
              variant="outlined"
            />
            <Chip
              label={`High Fee Due (>₹20k): ${alerts.HighDueCount ?? 0}`}
              sx={{
                border: `1px solid ${SURFACE_BORDER}`,
                backgroundColor: "rgba(25,118,210,0.08)",
                fontWeight: 800,
              }}
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
            {[
              { label: "Drill: Low Attendance", path: "/ManageAttendanceReport" },
              { label: "Drill: High Dues", path: "/StudentFeeReport" },
              { label: "Drill: Backlog Summary", path: "/ReportStudentBacklogsSummary" },
              { label: "Drill: Feedback Analytics", path: "/FeedbackFormAnalytics" },
            ].map((b) => (
              <Button
                key={b.path}
                size="small"
                variant="outlined"
                onClick={() => nav(b.path)}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 800,
                  borderColor: "rgba(25,118,210,0.35)",
                  "&:hover": { borderColor: "rgba(25,118,210,0.55)" },
                }}
              >
                {b.label}
              </Button>
            ))}
          </Box>
        </Paper>
      </Box>
    </Paper>
  );
}
